import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import get from 'lodash/get';
import some from 'lodash/some';
import find from 'lodash/find';
import useIsMobile from '../../../../hooks/useIsMobile';

import {
  Cell,
  Grid,
  GridContainer,
  Section
} from 'formlabs-web-components/Grid';
import { Header } from 'formlabs-web-components/Type';
import { Spinner } from 'formlabs-web-components/Navigation';
import { Picture } from 'formlabs-web-components/Media';

import ProductOptionControl, {
  ProductOptionListControl
} from '../../ProductOptionControl';
import CheckmarkList from '../../../content/CheckmarkList';
import DetailPrice from '../DetailPrice';
import ContactSalesBlock from './ContactSalesBlock';

const copyStrings = t => ({
  'Premium Service Plans': {
    title: t('Fuse and Sift Premium Service Plans'),
    subtitle: t('Maximum peace of mind'),
    copy: t(
      'This hands-on service plan offers the most complete coverage, with everything in the Standard Service Plan, plus customized on-site training and on-site visits at no extra charge.'
    ),
    checkList: [
      t('Customized on-site training'),
      t('On-site repairs included'),
      t('Direct phone and email support'),
      t('Annual preventative checkups'),
      t('Worry-free warranty')
    ]
  },
  'Standard Service Plans': {
    title: t('Fuse and Sift Standard Service Plans'),
    subtitle: t('Direct phone and email support'),
    copy: t(
      'Phone and email service for the Fuse 1 and Fuse Sift from certified SLS experts, with one annual on-site visit included and additional visits for an additional charge.'
    ),
    checkList: [
      t('Remote training'),
      t('Pay per on-site visit'),
      t('Direct phone and email support'),
      t('Annual preventative checkups'),
      t('Worry-free warranty')
    ]
  },
  'Fuse Premium Service Plans': {
    title: t('Fuse Premium Service Plans'),
    subtitle: t('Maximum peace of mind'),
    copy: t(
      'This hands-on service plan offers the most complete coverage, with everything in the Standard Service Plan, plus customized on-site training and on-site visits at no extra charge.'
    ),
    checkList: [
      t('Customized on-site training'),
      t('On-site repairs included'),
      t('Direct phone and email support'),
      t('Annual preventative checkups'),
      t('Worry-free warranty')
    ]
  },
  'Fuse Standard Service Plans': {
    title: t('Fuse Standard Service Plans'),
    subtitle: t('Direct phone and email support'),
    copy: t(
      'Phone and email service for the Fuse 1 from certified SLS experts, with one annual on-site visit included and additional visits for an additional charge.'
    ),
    checkList: [
      t('Remote training'),
      t('Pay per on-site visit'),
      t('Direct phone and email support'),
      t('Annual preventative checkups'),
      t('Worry-free warranty')
    ]
  }
});
const ServiceStep = ({
  activeBundle,
  bundleOptions,
  bundleOptionsDispatcher,
  cdnUrl,
  activeBundleData,
  activeBundleLoading,
  storeId
}) => {
  const mobile = useIsMobile(900);

  const [t] = useTranslation();
  const copy = copyStrings(t);
  const productOptions = get(activeBundle, 'bundle_product_options', []);
  // find if the sift is present in the bundle
  // let so this won't error if the user winds up here without going through the process
  // any quicker way to determine this is fine to be included
  const hasSift = bundleOptions.some(option =>
    option.product_links[0].name.includes('Fuse Sift')
  );
  // filter to just service options
  // filter to make sure that the correct half of options are shown
  const service_plans = productOptions
    .filter(option => option.title.includes('Service'))
    .filter(option => option.title.includes('Fuse') !== hasSift);
  const servicePlansIds = service_plans.map(g => g.option_id);
  const hasServicePlan = some(bundleOptions, option =>
    servicePlansIds.includes(option.option_id)
  );

  const fuseItem =
    bundleOptions && bundleOptions.length > 0 ? bundleOptions[0] : null;

  const activeDetails =
    fuseItem && fuseItem.product_links.length > 0
      ? fuseItem.product_links[0]
      : null;

  const numberOfPrinters = get(activeDetails, 'qty', 1);
  // set default option
  useEffect(() => {
    if (bundleOptions.length) {
      bundleOptionsDispatcher({
        type: 'add-radio',
        data: service_plans[0],
        id: service_plans[0].product_links[1].id,
        optionId: service_plans[0]['option_id'],
        linkedGroups: [service_plans[1].product_links[0].option_id]
      });
    }
  }, []);

  return (
    <GridContainer>
      <Grid direction="x" gutters="margin">
        <Cell>
          <Section backgroundColor="light-grey" padding="2rem">
            <Header level={3} displayAs={3} weight={700}>
              {hasSift
                ? t('Choose a Service Plan for Your Fuse 1 and Fuse Sift')
                : t('Choose a Service Plan for Your Fuse 1')}
            </Header>
            <p>
              {t(
                'Maximize your return, protect your investment, and eliminate unexpected costs with our Fuse 1 and Fuse Sift Service Plans.'
              )}
            </p>
          </Section>
          <Section
            marginBeforeSmall="1rem"
            marginBeforeMedium="1rem"
            marginBefore="1rem"
          >
            {service_plans.map(product => {
              if (!product.title.includes('Service')) {
                return null;
              }

              const activeBundleProduct = find(
                bundleOptions,
                option => option.option_id === product.option_id
              );

              const optionCopy = get(copy, product.title);

              return (
                <div
                  key={product.title}
                  style={{ borderTop: '1px solid #E6E6E6', paddingTop: '2rem' }}
                >
                  <Grid
                    direction={mobile ? 'y' : 'x'}
                    gutters="margin"
                    key={product.title}
                  >
                    <Cell mediumSize={12}>
                      <Header level={4} displayAs={4}>
                        {optionCopy && optionCopy.title}
                      </Header>
                      <div style={{ margin: '2rem 0' }}>
                        <ProductOptionListControl>
                          {product.product_links.map(p => {
                            const activeDetails = activeBundleProduct
                              ? find(
                                  activeBundleProduct.product_links,
                                  link => link.id === p.id
                                )
                              : '';

                            const optionData = find(
                              activeBundleData,
                              option => {
                                return option.sku === p.sku;
                              }
                            );
                            const imagePath = get(
                              optionData,
                              'attributes.image',
                              null
                            );
                            return (
                              <ProductOptionControl
                                key={p['option_id'] + p.name}
                                controlPosition="center"
                                imagePosition="left"
                                name={p['option_id']}
                                value={p.id}
                                title={
                                  numberOfPrinters > 1
                                    ? `${numberOfPrinters} x ${p.name}`
                                    : p.name
                                }
                                isActive={activeDetails}
                                hatTitle={
                                  p.name.includes('Premium Service 2') &&
                                  t('Recommended')
                                }
                                imageElement={
                                  !activeBundleLoading ? (
                                    imagePath ? (
                                      <div>
                                        <Picture
                                          largeImage={`${cdnUrl}${imagePath}`}
                                        />
                                      </div>
                                    ) : null
                                  ) : (
                                    <Spinner size={3} color="#0762c8" />
                                  )
                                }
                                onChange={() => {
                                  if (!activeDetails) {
                                    bundleOptionsDispatcher({
                                      type: 'add-radio',
                                      data: product,
                                      id: p.id,
                                      optionId: p['option_id'],
                                      remove: servicePlansIds
                                    });
                                  } else {
                                    if (!p.required) {
                                      bundleOptionsDispatcher({
                                        type: 'remove',
                                        id: product.id,
                                        optionId: p['option_id']
                                      });
                                    }
                                  }
                                }}
                                type="radio"
                                quantityOptions={{
                                  disabled: true,
                                  quantity: activeDetails ? 1 : 0,
                                  minQuantity: 1
                                }}
                              >
                                {p.required ? (
                                  t('Included')
                                ) : (
                                  <DetailPrice
                                    isRequired={p.required}
                                    price={p.price}
                                    qty={p.qty}
                                    storeId={storeId}
                                    optionData={optionData}
                                    perItemType={t('per plan')}
                                  />
                                )}

                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: get(
                                      product,
                                      'attributes.shortDescription',
                                      null
                                    )
                                  }}
                                />
                              </ProductOptionControl>
                            );
                          })}
                        </ProductOptionListControl>
                      </div>
                    </Cell>
                    <Cell mediumSize={8} mediumOffset={2} largeOffset={2}>
                      {optionCopy && (
                        <>
                          <Header level={5} displayAs={5} weight={700}>
                            {optionCopy.subtitle}
                          </Header>
                          <p>{optionCopy.copy}</p>
                          <CheckmarkList
                            width={1}
                            items={optionCopy.checkList}
                            light
                            narrow
                          />
                        </>
                      )}
                    </Cell>
                  </Grid>
                </div>
              );
            })}
            <div style={{ borderTop: '1px solid #E6E6E6', paddingTop: '2rem' }}>
              <Grid direction={mobile ? 'y' : 'x'} gutters="margin">
                <Cell mediumSize={12}>
                  <Header level={4} displayAs={4}>
                    {t('Decide Later')}
                  </Header>
                  <div style={{ margin: '2rem 0' }}>
                    <ProductOptionListControl>
                      <ProductOptionControl
                        controlPosition="center"
                        name="reset"
                        value="reset"
                        title={t('Choose a service plan later')}
                        isActive={!hasServicePlan}
                        onChange={e => {
                          const element = e.target;

                          if (element.checked) {
                            bundleOptionsDispatcher({
                              type: 'resetRadio',
                              linkedGroups: service_plans.map(g => g.option_id)
                            });
                          }
                        }}
                        type="radio"
                      >
                        <p>{t('Discuss your options with Formlabs sales')}</p>
                      </ProductOptionControl>
                    </ProductOptionListControl>
                  </div>
                </Cell>
                <Cell mediumSize={8} mediumOffset={2} largeOffset={2}>
                  <>
                    <Header level={5} displayAs={5} weight={700}>
                      {t('Discuss your options with a Formlabs representative')}
                    </Header>
                    <p>
                      {t(
                        'Can’t decide between the plans listed above? Choose this option to discuss service options later with one of our team members.'
                      )}
                    </p>
                  </>
                </Cell>
              </Grid>
            </div>
            <Grid direction={mobile ? 'y' : 'x'}>
              <Cell mediumSize={12}>
                <ContactSalesBlock />
              </Cell>
            </Grid>
          </Section>
        </Cell>
      </Grid>
    </GridContainer>
  );
};

export default ServiceStep;
